import { DeleteOutlined, DoubleRightOutlined } from '@ant-design/icons';
import { Button, Form, Input, Card, Typography, message, Select, Row, Col, Table } from 'antd';
import { useHistory } from 'react-router-dom';
import APIUtils from '../../api/APIUtils';
import WebLinks from '../../api/WebLinks';
import { useContext, useEffect, useState } from 'react';
import { CommonContext } from '../../Context';

const { Text } = Typography;

const AnalysisReport = (props) => {
  const commonContext=useContext(CommonContext);
  const buyerId = commonContext.common.buyerId;
  const history = useHistory();
  const [form] = Form.useForm();
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [reportTypes, setReportTypes] = useState([]);
  const [reportTypesLoading, setReportTypesLoading] = useState(false);
  const [automatedReports, setAutomatedReports] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortDesc, setSortDesc] = useState(null);

  useEffect(() => {
    getAnalysisReportTypes();
  }, []);
  useEffect(() => {
    if (buyerId) {
      getBuyerAnalysisReports(currentPage, pageSize);
    }
  }, [buyerId, currentPage, pageSize]);

  const getAnalysisReportTypes = async () => {
    setReportTypesLoading(true);
    try {
      const response = await APIUtils.apiCall('GET', WebLinks.GetAnalysisReportType);
      if (
        !response.isCatch &&
        response.resultObj.statusCode === '2000'
      ) {
        const options = response.resultObj.data.map((item) => ({
          label: item.reportType,
          value: item.id,
        }));
        setReportTypes(options);
      } else {
        message.error(response.resultObj.message || 'Failed to get report types.');
      }
    } catch (error) {
      message.error('Failed to get report types.');
    } finally {
      setReportTypesLoading(false);
    }
  };

  const getBuyerAnalysisReports = async (
    page = currentPage,
    limit = pageSize,
    sortField = sortBy,
    sortDescending = sortDesc
  ) => {
    setTableLoading(true);
    try {
      const params = new URLSearchParams({
        buyerId: buyerId,
        limit: limit.toString(),
        pageNo: page.toString(),
      });
      if (sortField) {
        params.append('sortBy', sortField);
        params.append('sortDesc', sortDescending.toString());
      }
      const url = `${WebLinks.GetBuyerAnalysisReport}?${params.toString()}`;
      const response = await APIUtils.apiCall('GET', url);
      if (
        !response.isCatch &&
        response.resultObj.statusCode === '2000'
      ) {
        const data = response.resultObj.data || [];
        setAutomatedReports(
          data.map((item) => ({
            ...item,
            key: item.id,
          }))
        );
        setTotalCount(response.resultObj.getCount || 0);
      } else {
        setAutomatedReports([]);
        setTotalCount(0);
      }
    } catch (error) {
      setAutomatedReports([]);
      setTotalCount(0);
      message.error('Failed to get reports.');
    } finally {
      setTableLoading(false);
    }
  };
  const onProceedClick = async (validEmails, values) => {
    const payload = {
      buyerId:buyerId,
      email:validEmails?.join(', '),
      analysisReportTypeId:values
    };

    const url = WebLinks.SendAnalysisReport;
    const response = await APIUtils.apiCall(
      'POST',
      url,
      JSON.stringify(payload)
    );
    if (
      !response.isCatch &&
      response.resultObj.statusCode === '2000'
    ) {
      message.success(response.resultObj.message);

      setEmailInput('');
      form.setFieldsValue({
        reportType:null
      })
      getBuyerAnalysisReports(currentPage, pageSize);
    }else{
      message.error(response.resultObj.message);
    }
  }

  const handleInputChange = (e) => {
    setEmailInput(e.target.value);
  };

  const validateEmail = (email) => {
    const emailRegex= /^(?!.*\s).*@.*$/;
    return emailRegex.test(email);
  };
  const handleAddEmails = () => {
    const newEmails = emailInput
      .split(/[\s,]+/)
      .filter((email) => email.trim() !== '');
    const validEmails = newEmails.filter(validateEmail);
    if (validEmails.length !== newEmails.length) {
      message.error('Please enter valid email addresses.');
    } else {
      setEmails([...emails, ...validEmails]);
      const values = form.getFieldValue('reportType');
      if(values){
        onProceedClick(validEmails, values);
      }else{
        message.error('Please select report type.');
      }
    }
  };
  const handleTableChange = (pagination, filters, sorter) => {
    const { current, pageSize: newPageSize } = pagination;
    let newSortBy = sortBy;
    let newSortDesc = sortDesc;
    console.log('sorter:', sorter);
    if (sorter?.columnKey) {
      newSortBy = sorter.columnKey;
      newSortDesc = sorter.order === 'descend';
    }
    setSortBy(newSortBy);
    setSortDesc(newSortDesc);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
      getBuyerAnalysisReports(
        1,
        newPageSize,
        newSortBy,
        newSortDesc
      );
    } else {
      setCurrentPage(current);
      getBuyerAnalysisReports(
        current,
        newPageSize,
        newSortBy,
        newSortDesc
      );
    }
  };
  const handleDeleteReport = async (id) => {
    try {
      const url = `${WebLinks.DeleteBuyerAnalysisReport}${id}`;
      const response = await APIUtils.apiCall('DELETE', url);
      if (
        !response.isCatch &&
        response.resultObj.statusCode === '2000'
      ) {
        message.success(response.resultObj.message);
        getBuyerAnalysisReports(currentPage, pageSize);
      } else {
        message.error(
          response.resultObj.message || 'Failed to delete report.'
        );
      }
    } catch (error) {
      message.error('Failed to delete report.');
    }
  };
  const columns = [
    {
      title: 'Report Type',
      dataIndex: 'reportType',
      key: 'AnalysisReportTypeId',
      columnKey: 'AnalysisReportTypeId',
      width: '40%',
      sorter: true,
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'Email',
      columnKey: 'Email',
      width: '40%',
      sorter: true,
    },
    {
      title: 'Action',
      key: 'delete',
      width: '20%',
      render: (_, record) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteReport(record?.id)}
        />
      ),
    },
  ];

  return (
    <div>
      <div>
        {/* <p className="mb-3">Send reports to these emails</p> */}
        <p className="mb-3 font-bold italic">
          Info note: "Delayed reports are sent 2 months after end date to
          allow time to hire"
        </p>
      </div>
      <div className="w-full max-w-7xl mt-6">
        <Form form={form} autoComplete="off">
          <Row gutter={[16, 16]} align="top">
            {/* Report Type */}
            <Col xs={24} sm={24} md={10} lg={10}>
              <Form.Item name="reportType" className="mb-0">
                <Select
                  options={reportTypes}
                  placeholder="Select report type"
                  className="w-full"
                  loading={reportTypesLoading}
                />
              </Form.Item>
            </Col>

            {/* Email */}
            <Col xs={24} sm={24} md={9} lg={10}>
              <Form.Item className="mb-0">
                <Input
                  value={emailInput}
                  onChange={handleInputChange}
                  placeholder="Enter email addresses"
                />
              </Form.Item>

              <div className="mt-1 text-sm font-semibold text-gray-500">
                * separate by commas if more than one
              </div>
            </Col>
            <Col xs={24} sm={24} md={5} lg={4}>
              <Button
                type="primary"
                onClick={handleAddEmails}
                block
                className="whitespace-nowrap"
              >
                SAVE REPORT <DoubleRightOutlined />
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
      <div className="mt-8">
        <div className="uppercase font-bold text-xl mb-4">
          Automated Reports
        </div>
        <Table
          columns={columns}
          dataSource={automatedReports}
          loading={tableLoading}
          bordered={false}
          rowClassName="align-middle"
          className="custom-table"
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
          }}
        />
      </div>
    </div>
  );
};
export default AnalysisReport;
